import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import toast, { Toaster } from 'react-hot-toast'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ToolTip } from '../../components/toolTip/ToolTip'
import { translations } from '../../configs/translations'
import { RaceTypes } from '../../enums/RaceTypes'
import { VehicleModulesResearchResponse } from '../../models/response/VehicleModulesResearchResponse'
import { UserProfile } from '../../models/UserProfile'
import { AnyModuleCharacteristics, ModuleTypes, ModuleView } from '../../models/VehicleModule'
import { VehicleModulesService } from '../../services/VehicleModulesService'
import { useStore } from '../../store/store'
import { Header } from '../main/header/Header'
import { ModuleSpec } from './moduleSpec/ModuleSpec'
import './ModulesResearch.css'
import { ModulesTree } from './modulesTree/ModulesTree'


export const ModuleTreeConfig = {
	MAIN_LEFT_PADDING: 15,
	CAR_NODE_WIDTH: 215,
	GAP: 120,
	PICTURE_WIDTH: 666,
	PICTURE_HEIGHT: 600,
	VERTICAL_PADDING: 68,
	MODULES_VERTICAL_GAP: 72.5,
	MODULES_HORIZONTAL_GAP: 65,
	CONTENT_ZONE_WIDTH: 330
}

export const ModulesResearch = () => {

	const [searchParams] = useSearchParams()
	const vehicleIdStr = searchParams.get('id')
	const vehicleId = Number(vehicleIdStr)

	const [userProfile, setUserProfile] = useState<UserProfile | null>(JSON.parse(localStorage.getItem('profile') || 'null'))
	const language = useStore(state => state.language) as keyof typeof translations
	const t = translations[language]

	const [data, setData] = useState<VehicleModulesResearchResponse | null>(null)
	const [areAllModulesBought, setAreAllModulesBought] = useState(false)
	const [triggerUpdate, setTriggerUpdate] = useState(0)
	const navigate = useNavigate()
	const isLoading = useRef(false)

	const [mouseX, setMouseX] = useState(0)
	const [mouseY, setMouseY] = useState(0)
	const [hoveredModule, setHoveredModule] = useState<ModuleView | null>(null)

	const handleModuleHover = useCallback((event: React.MouseEvent, module: ModuleView) => {
		setMouseX(event.clientX)
		setMouseY(event.clientY)
		setHoveredModule(module)
	}, [])

	const handleModuleLeave = useCallback(() => setHoveredModule(null), [])
	useEffect(() => {
		const fetchData = async () => {
			if (isLoading.current) return
			isLoading.current = true
			VehicleModulesService.getTree(vehicleId)
				.then((resp) => {
					setData(resp)
					setAreAllModulesBought(
						resp.vehicle.bought &&
						resp.modules.length > 0 &&
						resp.modules.every((module) => module.bought))
					isLoading.current = false
					setUserProfile(null)
				})
				.catch((error) => {
					isLoading.current = false
					toast.error(
						error &&
							error.response &&
							error.response.data &&
							t[error.response.data as keyof typeof t]
							? t[error.response.data as keyof typeof t]
							: t.error_unknown,
						{ className: 'notification-error' }
					)
				})
		}

		fetchData()
	}, [vehicleId, triggerUpdate])

	return (
		<div>
			<Header
				profile={userProfile}
				setProfile={setUserProfile}
				selectedMode={RaceTypes.RANKED}
				selectedVehicle={null}
				title={t.research}
			/>

			<div className="research-tree-content-zone">
				<div className="research-tree-main-header">
					{areAllModulesBought ? (
						<div className="research-tree-main-header-title">
							<img
								className="research-tree-main-header-title-icon"
								src='/icons/64px/legendary_vehicle.webp'
								alt="legendary-vehicle"
							/>
							<h1>{data?.vehicle.vehicle.brand} {data?.vehicle.vehicle.name}</h1>
						</div>) : (
						<div className="research-tree-cars-main-header-title">
							<h1>{data?.vehicle.vehicle.brand} {data?.vehicle.vehicle.name}</h1>
						</div>
					)
					}
				</div>

				<ModulesTree
					data={data}
					setTriggerUpdate={setTriggerUpdate}
					navigate={navigate}
					onModuleHover={handleModuleHover}
					onModuleLeave={handleModuleLeave}
				/>
			</div>

			{createPortal(
				<ToolTip
					style='left'
					position={{ x: mouseX, y: mouseY }}
					content={<ModuleSpec
						name={hoveredModule?.name ?? ''}
						moduleKey={hoveredModule?.moduleKey ?? ''}
						type={hoveredModule?.type ?? ModuleTypes.ENGINE}
						level={hoveredModule?.level ?? -1}
						icon={hoveredModule?.icon ?? ''}
						price={hoveredModule?.price ?? -1}
						xpPrice={hoveredModule?.xpPrice ?? -1}
						isResearched={hoveredModule?.isResearched ?? false}
						isBought={hoveredModule?.isBought ?? false}
						isEquipped={hoveredModule?.isEquipped ?? false}
						technicalCharacteristics={hoveredModule?.technicalCharacteristics ?? {} as AnyModuleCharacteristics}
						effects={hoveredModule?.effects ?? []}
					/>}
					isShown={hoveredModule !== null}
				/>,
				document.body
			)}

			<Toaster
				toastOptions={{
					duration: 2000,
				}}
				position="bottom-center"
			/>

			<div className='research-tree-background'>
				<img
					src="/research/research-tree-background.webp"
					alt="research-tree-background"
				/>
			</div>
		</div>
	)
}